import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, allArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || '');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const categoryArticles = currentCategory.articles
    .map((aSlug) => allArticles.find((a) => a.slug === aSlug))
    .filter(Boolean);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb />

        <section className="w-full pt-4 pb-8 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className={`${currentCategory.icon} text-xl text-primary-600`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-sm md:text-base text-foreground-600 max-w-3xl">
                {currentCategory.description}
              </p>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-10 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => {
                const imgIdx = categoryArticles.indexOf(article);
                const images = [
                  'https://readdy.ai/api/search-image?query=Scientific%20illustration%20of%20a%20wild%20mushroom%20cross%20section%20showing%20detailed%20anatomy%20with%20labels%2C%20clean%20cream%20background%2C%20educational%20botanical%20style%2C%20precise%20linework%20and%20soft%20natural%20watercolor%2C%20warm%20earthy%20color%20scheme&width=600&height=400&seq=cat-img-01&orientation=landscape',
                  'https://readdy.ai/api/search-image?query=Laboratory%20setup%20with%20glass%20test%20tubes%20and%20mushroom%20samples%20on%20a%20clean%20wooden%20table%2C%20natural%20light%20from%20window%2C%20scientific%20research%20aesthetic%2C%20warm%20earthy%20and%20cream%20tones&width=600&height=400&seq=cat-img-02&orientation=landscape',
                  'https://readdy.ai/api/search-image?query=Spore%20print%20of%20a%20mushroom%20on%20white%20paper%20showing%20beautiful%20radial%20pattern%2C%20artistic%20nature%20photography%2C%20minimalist%20composition%2C%20warm%20natural%20lighting%2C%20cream%20and%20brown%20color%20palette&width=600&height=400&seq=cat-img-03&orientation=landscape',
                  'https://readdy.ai/api/search-image?query=Closeup%20of%20a%20freshly%20cut%20wild%20mushroom%20showing%20inner%20flesh%20texture%20and%20color%2C%20on%20a%20wooden%20surface%2C%20natural%20light%2C%20rich%20detail%2C%20warm%20earthy%20photography&width=600&height=400&seq=cat-img-04&orientation=landscape',
                  'https://readdy.ai/api/search-image?query=Person%20gently%20smelling%20a%20fresh%20wild%20mushroom%20in%20a%20forest%20setting%2C%20soft%20natural%20light%2C%20shallow%20depth%20of%20field%2C%20editorial%20nature%20photography%2C%20warm%20earthy%20tones&width=600&height=400&seq=cat-img-05&orientation=landscape',
                  'https://readdy.ai/api/search-image?query=A%20mushroom%20cap%20with%20visible%20insect%20bite%20marks%20on%20forest%20floor%2C%20small%20insect%20nearby%2C%20soft%20natural%20lighting%2C%20detailed%20macro%20photography%2C%20warm%20natural%20color%20palette&width=600&height=400&seq=cat-img-06&orientation=landscape',
                ];
                const thumbImg = images[imgIdx % images.length];

                return (
                  <Link
                    key={article?.slug}
                    to={`/article/${article?.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/80 transition-all duration-300"
                  >
                    <div className="aspect-[3/2] overflow-hidden">
                      <img
                        src={thumbImg}
                        alt={article?.title || ''}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 md:p-6">
                      <div className="flex items-center gap-3 mb-2.5 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article?.readTime || 5} 分钟阅读
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article?.publishDate || ''}
                        </span>
                      </div>
                      <h2 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article?.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article?.excerpt}
                      </p>
                      <div className="mt-4 flex items-center gap-1 text-primary-600 text-sm font-medium">
                        阅读全文
                        <i className="ri-arrow-right-line text-xs"></i>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}